import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AuthService from '../services/auth/storage'; // Manage tokens
import {
  LOGIN_MUTATION,
  SIGNUP_MUTATION,
  GOOGLE_AUTH_MUTATION,
  RECORD_AI_CONSENT,
  UPDATE_PROFILE,
} from '../services/apollo/mutations/userMutations';
import client from '../services/apollo/apolloClient/index';
import {
  CONSENT_VERSION,
  hasValidAiConsent,
  recordLocalConsent,
} from '../utils/aiConsent';
// import { setCoordinates, setLocationName } from '../slices/locationSlice';
import { resetTemporaryCoordinates } from '../slices/locationSlice';
import { resetFavorites } from '../slices/favoritesSlice';
import { resetMessages } from "../slices/messagesSlice";
import { fetchBlockedUsers, clearBlockedUsers } from './blocksSlice';
// import { getUserCoordinates, reverseGeocode } from '../utils/geolocation';
import { jwtDecode } from 'jwt-decode';

const syncAiConsentOnLogin = async (user) => {
  try {
    const serverConsented =
      user?.aiConsent === true && user?.consentVersion === CONSENT_VERSION;

    if (serverConsented) {
      recordLocalConsent();
      return;
    }

    if (hasValidAiConsent()) {
      await client.mutate({
        mutation: RECORD_AI_CONSENT,
        variables: { aiConsent: true, consentVersion: CONSENT_VERSION },
      });
    }
  } catch (error) {
    console.warn('AI consent sync on login failed:', error?.message || error);
  }
};

// Thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (formData, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: { ...formData },
      });

      const token = data?.login?.token;
      const user = data?.login?.user;

      if (token) {
        AuthService.storeToken(token); // Store token

        // Fetch and dispatch coordinates
        // const coordinates = await getUserCoordinates();
        // dispatch(setCoordinates(coordinates));

        // const [lng, lat] = coordinates;
        // const locationName = await reverseGeocode(lat, lng);
        // dispatch(setLocationName(locationName));

        await syncAiConsentOnLogin(user);

        dispatch(fetchBlockedUsers());

        return user; // Return user data to update the store
      } else {
        return rejectWithValue('Invalid credentials');
      }
    } catch (error) {
      console.error('Login error: ', error);
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

// Thunk for signup
export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async (formData, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: SIGNUP_MUTATION,
        variables: { ...formData },
      });

      const token = data?.signup?.token;
      const user = data?.signup?.user;

      if (token) {
        AuthService.storeToken(token);

        // Fetch and dispatch coordinates
        // const coordinates = await getUserCoordinates();
        // dispatch(setCoordinates(coordinates));

        // const [lng, lat] = coordinates;
        // const locationName = await reverseGeocode(lat, lng);
        // dispatch(setLocationName(locationName));

        if (user?.aiConsent && user?.consentVersion === CONSENT_VERSION) {
          recordLocalConsent();
        }

        return user; // Return user data to update the store
      } else {
        return rejectWithValue('Signup failed, please check your details');
      }
    } catch (error) {
      console.error('Signup error: ', error);
      return rejectWithValue(error.message || 'Signup failed');
    }
  }
);

// Thunk for Google sign-in / sign-up
export const googleAuthUser = createAsyncThunk(
  'auth/googleAuthUser',
  async (variables, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: GOOGLE_AUTH_MUTATION,
        variables: { ...variables },
      });

      const token = data?.googleAuth?.token;
      const user = data?.googleAuth?.user;

      if (token) {
        AuthService.storeToken(token);

        await syncAiConsentOnLogin(user);

        dispatch(fetchBlockedUsers());

        return user;
      } else {
        return rejectWithValue('Google sign-in failed');
      }
    } catch (error) {
      console.error('Google auth error: ', error);
      const message =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Google sign-in failed';
      return rejectWithValue(message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_PROFILE,
        variables: { ...formData },
      });

      const token = data?.updateProfile?.token;
      const user = data?.updateProfile?.user;

      if (token && user) {
        AuthService.storeToken(token);
        return user;
      }

      return rejectWithValue('Failed to update profile');
    } catch (error) {
      console.error('Update profile error: ', error);
      const message =
        error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to update profile';
      return rejectWithValue(message);
    }
  }
);

export const logoutUser = () => (dispatch) => {
  dispatch(resetTemporaryCoordinates());
  dispatch(resetFavorites());
  dispatch(resetMessages());
  dispatch(clearBlockedUsers());
  AuthService.removeToken();

  dispatch(authSlice.actions.logout());
};

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isAuthenticated: !!AuthService.getToken(),
    isInitialized: false,
    user: null,
    loading: false,
    error: null,
  },
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.user = null;
    },
    /**
     * Initializes the authentication state based on the presence of a token.
     */
    _setAuthFromToken(state) {
      const token = AuthService.getToken();
    
      if (!token) {
        state.isAuthenticated = false;
        state.user = null;
      } else {
        try {
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000; // Convert to seconds
    
          if (decodedToken.exp < currentTime) {
            state.isAuthenticated = false;
            state.user = null;
          } else {
            state.isAuthenticated = true;
            state.user = decodedToken?.data || null;
          }
        } catch (error) {
          console.error("Failed to decode token:", error.message);
          state.isAuthenticated = false;
          state.user = null;
        }
      }
    
      state.isInitialized = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(googleAuthUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleAuthUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(googleAuthUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = { ...state.user, ...action.payload };
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { logout } = authSlice.actions;

export const initializeAuth = () => (dispatch, getState) => {
  dispatch(authSlice.actions._setAuthFromToken());

  const { auth } = getState();
  if (auth.isAuthenticated) {
    dispatch(fetchBlockedUsers());
  }
};

export default authSlice.reducer;
